import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Download, RefreshCw, History, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TYPE_LABELS = {
  opening_stock_adjustment: 'Opening Stock Edit',
  stock_correction: 'Stock Correction',
  purchase_reversal: 'GRN Reversal',
};

const TYPE_CLASSES = {
  opening_stock_adjustment: 'bg-blue-100 text-blue-800 border-blue-300',
  stock_correction: 'bg-amber-100 text-amber-800 border-amber-300',
  purchase_reversal: 'bg-red-100 text-red-800 border-red-300',
};

const formatDateTime = (v) => {
  if (!v) return '-';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const fmtQty = (q) => {
  const n = Number(q || 0);
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(2)}`;
};

export default function StockAdjustmentsRegisterPage() {
  const { user, getAuthHeader } = useAuth();
  const isAllowed = ['owner', 'admin', 'super_admin'].includes(user?.role);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [itemFilter, setItemFilter] = useState('all');
  const [createdByFilter, setCreatedByFilter] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [search, setSearch] = useState('');

  const fetchRows = async () => {
    if (!isAllowed) { setLoading(false); return; }
    setLoading(true);
    try {
      const params = {};
      if (typeFilter && typeFilter !== 'all') params.movement_type = typeFilter;
      if (itemFilter && itemFilter !== 'all') params.item_id = itemFilter;
      if (createdByFilter && createdByFilter !== 'all') params.created_by = createdByFilter;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const res = await axios.get(`${API_URL}/admin/stock-adjustments`, {
        params, headers: getAuthHeader(),
      });
      setRows(res.data?.rows || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load stock adjustments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRows(); /* eslint-disable-next-line */ }, [typeFilter, itemFilter, createdByFilter, fromDate, toDate]);

  const itemOptions = useMemo(() => {
    const seen = new Map();
    rows.forEach((r) => {
      if (r.item_id && !seen.has(r.item_id)) seen.set(r.item_id, `${r.item_code || '?'} — ${r.item_name || ''}`);
    });
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);

  const createdByOptions = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => { if (r.created_by) s.add(r.created_by); });
    return Array.from(s).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) =>
      (r.item_code || '').toLowerCase().includes(q)
      || (r.item_name || '').toLowerCase().includes(q)
      || (r.remarks || '').toLowerCase().includes(q)
      || (r.reference_id || '').toLowerCase().includes(q)
    );
  }, [rows, search]);

  const totals = useMemo(() => {
    const t = { total: filtered.length, by_type: {}, net_qty: 0 };
    filtered.forEach((r) => {
      t.by_type[r.movement_type] = (t.by_type[r.movement_type] || 0) + 1;
      t.net_qty += Number(r.quantity || 0);
    });
    return t;
  }, [filtered]);

  const exportCsv = () => {
    const header = ['Date', 'Item Code', 'Item Name', 'Category', 'Movement Type', 'Quantity', 'Reference Type', 'Reference ID', 'Remarks', 'Created By'];
    const body = filtered.map((r) => [
      formatDateTime(r.created_at),
      r.item_code,
      r.item_name,
      r.item_category,
      TYPE_LABELS[r.movement_type] || r.movement_type,
      r.quantity,
      r.reference_type,
      r.reference_id,
      (r.remarks || '').replace(/[\r\n]+/g, ' ').replace(/,/g, ';'),
      r.created_by,
    ]);
    const csv = [header, ...body].map((row) => row.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stock-adjustments-register-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!isAllowed) {
    return (
      <DashboardLayout>
        <Card className="max-w-xl mx-auto mt-12" data-testid="stock-adjustments-access-denied">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Access Denied
            </CardTitle>
            <CardDescription>This register is only available to owner or admin roles.</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">Current role: <span className="font-mono">{user?.role || 'unknown'}</span></p>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-4" data-testid="stock-adjustments-register-page">
        <div className="flex justify-between items-start flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <History className="w-6 h-6" />
              Stock Adjustments Register
            </h1>
            <p className="text-muted-foreground text-sm">
              Audit trail of every direct stock change — opening-stock edits, GRN reversals, manual corrections.
              These events bypass the normal transaction flow and are intentionally excluded from the Item Nett Status page.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchRows} disabled={loading} data-testid="stock-adj-refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button variant="outline" onClick={exportCsv} disabled={!filtered.length} data-testid="stock-adj-export-csv-btn">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        <Card>
          <CardContent className="pt-4">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground">Type</label>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger data-testid="stock-adj-type-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="opening_stock_adjustment">Opening Stock Edit</SelectItem>
                    <SelectItem value="stock_correction">Stock Correction</SelectItem>
                    <SelectItem value="purchase_reversal">GRN Reversal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Item</label>
                <Select value={itemFilter} onValueChange={setItemFilter}>
                  <SelectTrigger data-testid="stock-adj-item-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Items</SelectItem>
                    {itemOptions.map(([id, label]) => <SelectItem key={id} value={id}>{label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Created By</label>
                <Select value={createdByFilter} onValueChange={setCreatedByFilter}>
                  <SelectTrigger data-testid="stock-adj-createdby-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any</SelectItem>
                    {createdByOptions.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">From</label>
                <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="stock-adj-from-date" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">To</label>
                <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="stock-adj-to-date" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Search</label>
                <Input placeholder="item, ref, remarks…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="stock-adj-search" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-6 flex-wrap">
              <span>{totals.total} event{totals.total === 1 ? '' : 's'}</span>
              {Object.entries(totals.by_type).map(([t, c]) => (
                <Badge key={t} className={TYPE_CLASSES[t] || ''}>
                  {TYPE_LABELS[t] || t}: {c}
                </Badge>
              ))}
              <span className="ml-auto text-sm text-muted-foreground">Net qty delta: <span className={`font-mono font-bold ${totals.net_qty >= 0 ? 'text-green-700' : 'text-red-700'}`}>{fmtQty(totals.net_qty)}</span></span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center py-8 text-muted-foreground">Loading events…</p>
            ) : filtered.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground" data-testid="stock-adj-empty">
                <History className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No adjustment events match the current filters</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Item Code</TableHead>
                      <TableHead>Item Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Quantity</TableHead>
                      <TableHead>Reference</TableHead>
                      <TableHead>Remarks</TableHead>
                      <TableHead>Created By</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((r) => (
                      <TableRow key={r.id || `${r.item_id}-${r.created_at}`} data-testid={`stock-adj-row-${r.id}`}>
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{formatDateTime(r.created_at)}</TableCell>
                        <TableCell className="font-mono">{r.item_code}</TableCell>
                        <TableCell className="text-sm">{r.item_name}</TableCell>
                        <TableCell>
                          <Badge className={TYPE_CLASSES[r.movement_type] || ''}>
                            {TYPE_LABELS[r.movement_type] || r.movement_type}
                          </Badge>
                        </TableCell>
                        <TableCell className={`text-right font-mono font-bold ${Number(r.quantity) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {fmtQty(r.quantity)}
                        </TableCell>
                        <TableCell className="text-xs">
                          {r.reference_type && <div className="text-muted-foreground">{r.reference_type}</div>}
                          {r.reference_id && <div className="font-mono">{r.reference_id.length > 20 ? r.reference_id.slice(0, 20) + '…' : r.reference_id}</div>}
                        </TableCell>
                        <TableCell className="text-sm max-w-xs truncate" title={r.remarks}>{r.remarks || '-'}</TableCell>
                        <TableCell className="text-sm">{r.created_by || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
